import React from 'react';
import Moment from 'moment';
import Card from 'react-bootstrap/Card';
import { downloadAttachment } from '../../common/services/next-gen/PatientTrackingService';
import '../../css/card.css';

/**
 * PatientTrackingActionsList Component
 * 
 * Displays a list of patient tracking actions in the result section.
 * Shows contact date, contact type, contacted by, and notes for each action.
 * 
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.actions - Array of patient tracking actions
 * @param {boolean} [props.showHeader=true] - Whether to show the section header
 * @param {string} [props.dateFormat='MM/DD/YYYY'] - Moment.js date format string
 * @param {Function} [props.onActionClick] - Optional callback when an action is clicked
 * 
 * @example
 * <PatientTrackingActionsList 
 *   actions={trackingActions}
 *   showHeader={true}
 *   onActionClick={(action) => console.log(action)}
 * />
 */
export const PatientTrackingActionsList = ({ 
    actions = [], 
    showHeader = true,
    dateFormat = 'MM/DD/YYYY',
    onActionClick,
    mostRecentActionId
}) => {
    /**
     * Handle downloading attachment
     */
    const handleDownloadAttachment = async (e, action) => {
        e.stopPropagation(); // Prevent row click
        
        try {
            const actionId = action.trackingActionId || action.id || action.patientTrackingActionId || action.actionId;
            const response = await downloadAttachment(actionId);
            
            // Create blob and download
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get filename from response headers or use stored filename
            const contentDisposition = response.headers['content-disposition'];
            let filename = action.nonCompliantAttachmentFileName || 'attachment';
            
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading attachment:', error);
            alert('Failed to download attachment');
        }
    };
    if (!actions || actions.length === 0) {
        return null;
    }

    // Sort actions by contact date, newest first
    const sortedActions = [...actions].sort((a, b) => 
        new Date(b.contactDate || b.createdDate) - new Date(a.contactDate || a.createdDate)
    );

    return (
        <div className="patient-tracking-actions-list mt-3">
            {showHeader && (
                <div className="row">
                    <div className="col-12">
                        <h6><strong>Patient Tracking Actions</strong></h6>
                    </div>
                </div>
            )}
            
            <div className="row">
                <div className="col-12">
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>Contact Date</th>
                                <th>Entry Date</th>
                                <th>Contact Type</th>
                                <th>Contacted By</th>
                                <th>Notes</th>
                                <th style={{ width: '50px', textAlign: 'center' }}>Attachment</th>
                            </tr>
                        </thead>
                        <tbody>
                        {sortedActions.map((action, index) => {
                                const actionId = action.trackingActionId || action.id || action.patientTrackingActionId || action.actionId;
                                const isMostRecent = mostRecentActionId && actionId === mostRecentActionId;
                                const isClickable = onActionClick && isMostRecent;
                                return (
                                <tr 
                                    key={actionId || index}
                                    onClick={() => isClickable && onActionClick(action)}
                                    style={isClickable ? { cursor: 'pointer' } : {}}
                                    title={onActionClick && !isMostRecent ? 'Only the most recent action can be edited' : undefined}
                                >
                                    <td>{Moment(action.contactDate || action.createdDate).format(dateFormat)}</td>
                                    <td>{Moment(action.createdDate).format(dateFormat)}</td>
                                    <td>{action.contactTypeName || 'N/A'}</td>
                                    <td>{action.contactedByUserName || 'Practice'}</td>
                                    <td className="text-truncate" style={{ maxWidth: '300px' }}>
                                        {action.notes || '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {action.nonCompliantAttachmentFileName && (
                                            <i 
                                                className="fas fa-paperclip" 
                                                style={{ 
                                                    color: '#f30079', 
                                                    cursor: 'pointer',
                                                    fontSize: '1.1rem'
                                                }}
                                                onClick={(e) => handleDownloadAttachment(e, action)}
                                                title={`Download: ${action.nonCompliantAttachmentFileName}`}
                                            />
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PatientTrackingActionsList;
